// Lib
import React from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { isEmpty, trim, debounce, isObject } from 'lodash/fp';

// Utils
import { asObject } from '../../../../../../../../common/utils/immutableHelper';
import { getElement } from '../../../../../../../../common/elements/utils/elementTraversalUtils';
import {
    getIsWelcomeMessageEnabled,
    getTitle,
    getWelcomeMessageProp,
    getWelcomeMessageText,
} from '../../../../../../../../common/elements/utils/elementPropertyUtils';
import userTextValidator from '../../../../../../../../common/validation/validators/type/userTextValidator';

// Selectors
import { getCurrentUserId } from '../../../../../../../user/currentUserSelector';
import { getElements } from '../../../../../../../element/selectors/elementSelector';

// Actions
import { updateElement } from '../../../../../../../../common/elements/elementActions';

// Hooks
import useFocusOnTrue from '../../../../../../../utils/react/useFocusOnTrue';

// Components
import ToggleInput from '../../../../../../../components/form/fields/ToggleInput';
import TooltipSource from '../../../../../../../components/tooltips/TooltipSource';
import SettingsRow from '../../../../../../../user/account/accountModal/components/SettingsRow';
import StyledPopupInputContainer from '../../../../../../../components/form/fields/StyledPopupInputContainer';
import StyledFieldInfoText from '../../../../../../../components/form/fields/StyledFieldInfoText';
import ToggleContents from '../../../../../../../components/form/fields/ToggleContents';

// Constants
import { TIMES } from '../../../../../../../../common/utils/timeUtil';
import { TooltipPositions } from '../../../../../../../components/tooltips/tooltipConstants';

// Styles
import './WelcomeMessageToggle.scss';

const MAX_CHARS = 300;
const TRANSITION_DURATION = 200;

const saveWelcomeMessage =
    ({ elementId, welcomeMessage }) =>
    (dispatch, getState) => {
        const state = getState();
        const elements = getElements(state);

        const currentUserId = getCurrentUserId(state);

        const element = getElement(elements, elementId);
        const isEnabled = getIsWelcomeMessageEnabled(element);

        // Turn off the welcome message if there's no message text
        if (isEnabled && isEmpty(welcomeMessage)) {
            return dispatch(
                updateElement({
                    id: elementId,
                    changes: {
                        welcomeMessage: {
                            enabled: false,
                            textContent: '',
                            modifier: null,
                        },
                    },
                    sync: true,
                }),
            );
        }

        const existingText = getWelcomeMessageText(element);
        const updatedText = trim(welcomeMessage);

        // No changes, so don't dispatch any updates
        if (existingText === updatedText) return;

        return dispatch(
            updateElement({
                id: elementId,
                changes: {
                    welcomeMessage: {
                        enabled: isEnabled,
                        textContent: updatedText,
                        modifier: currentUserId,
                    },
                },
                sync: true,
            }),
        );
    };

const setWelcomeMessageEnabled =
    ({ elementId, isEnabled }) =>
    (dispatch, getState) => {
        const state = getState();
        const elements = getElements(state);

        const element = getElement(elements, elementId);
        const existingWelcomeMessageObj = asObject(getWelcomeMessageProp(element));

        const existingWelcomeMessage = isObject(existingWelcomeMessageObj) ? existingWelcomeMessageObj : {};

        return dispatch(
            updateElement({
                id: elementId,
                changes: {
                    welcomeMessage: {
                        ...existingWelcomeMessage,
                        enabled: isEnabled,
                    },
                },
                sync: true,
            }),
        );
    };

const mapDispatchToProps = (dispatch) => ({
    dispatchSetWelcomeMessageEnabled: ({ elementId, isEnabled }) =>
        dispatch(
            setWelcomeMessageEnabled({
                elementId,
                isEnabled,
            }),
        ),
    dispatchSaveWelcomeMessage: ({ elementId, welcomeMessage }) =>
        dispatch(
            saveWelcomeMessage({
                elementId,
                welcomeMessage,
            }),
        ),
});

const WelcomeMessageToggle = (props) => {
    const {
        isPublished,
        canModifySettings,
        currentBoard,
        currentBoardId,
        showSharedAncestorBoardLink,
        publishedAncestorBoard,

        dispatchSaveWelcomeMessage,
        dispatchSetWelcomeMessageEnabled,
    } = props;

    const isEnabled = getIsWelcomeMessageEnabled(currentBoard);
    const persistedWelcomeMessage = getWelcomeMessageText(currentBoard);

    // Manage the enabled / disabled state independently so it won't toggle off when there's no message
    const [enabled, setEnabled] = React.useState(isEnabled);
    const [welcomeMessage, setWelcomeMessage] = React.useState(persistedWelcomeMessage || '');
    const [welcomeMessageError, setWelcomeMessageError] = React.useState('');

    const welcomeMessageRef = React.useRef();
    welcomeMessageRef.current = welcomeMessage;

    const textAreaRef = React.useRef();

    // Focus the welcome message text area when it's enabled
    useFocusOnTrue(textAreaRef, enabled, TRANSITION_DURATION + 40);

    // Need to prevent paste events from triggering the global paste handler
    React.useEffect(() => {
        const textAreaNode = textAreaRef.current;

        if (!textAreaNode || !enabled) return;

        const handlePaste = (event) => {
            // cancel paste
            event.preventDefault();
            event.stopPropagation();

            // get text representation of clipboard
            const text = (event.originalEvent || event).clipboardData.getData('text/plain');

            // insert text manually
            document.execCommand('insertHTML', false, text);
        };

        textAreaNode.addEventListener('paste', handlePaste);

        return () => textAreaNode.removeEventListener('paste', handlePaste);
    }, [enabled]);

    const persistWelcomeMessage = () => {
        dispatchSaveWelcomeMessage({
            elementId: currentBoardId,
            welcomeMessage: welcomeMessageRef.current,
        });
    };

    // On unmount, check if the welcome message has changed
    React.useEffect(() => persistWelcomeMessage, []);

    const onToggle = (event) => {
        setEnabled(event.target.checked);
        dispatchSetWelcomeMessageEnabled({ elementId: currentBoardId, isEnabled: event.target.checked });
    };

    // Save the changes every on a debounce when the user is typing
    const debouncedPersist = React.useCallback(debounce(500, persistWelcomeMessage), []);

    const onMessageChange = (event) => {
        const text = event.target.value || '';
        const updateValue = text.substr(0, MAX_CHARS);

        const validationResult = userTextValidator(updateValue, {
            name: 'welcomeMessage',
            type: 'userText',
            required: false,
            maxLength: MAX_CHARS,
        });

        setWelcomeMessageError(validationResult ? validationResult.message : null);

        setWelcomeMessage(updateValue);

        if (!validationResult) {
            debouncedPersist();
        }
    };

    const charCount = (welcomeMessage && welcomeMessage.length) || 0;

    return (
        <div className="WelcomeMessageToggle">
            <SettingsRow disabled={!isPublished} className="secondary-row" label="Set a welcome message">
                <TooltipSource
                    className="share-link-toggle-tooltip small-text"
                    enabled={!!showSharedAncestorBoardLink}
                    triggerOnClick
                    position={TooltipPositions.BOTTOM}
                    duration={TIMES.SECOND * 3}
                    tooltipText={`Change this setting in: ${getTitle(publishedAncestorBoard)}`}
                >
                    <ToggleInput disabled={!canModifySettings} active={enabled} onChange={onToggle} />
                </TooltipSource>
            </SettingsRow>
            <ToggleContents active={enabled} duration={TRANSITION_DURATION} disabled={!canModifySettings}>
                <div className="setting-contents">
                    <StyledPopupInputContainer inputRef={textAreaRef}>
                        <textarea
                            ref={textAreaRef}
                            disabled={!canModifySettings}
                            className="message-text-area"
                            value={welcomeMessage}
                            placeholder="Add a message that people will see when they first arrive at this board."
                            onChange={onMessageChange}
                        />
                    </StyledPopupInputContainer>
                    <StyledFieldInfoText className="welcome-status">
                        <div className="message-count">
                            <span className="character-count">{charCount}</span> / {MAX_CHARS} characters
                        </div>
                        {welcomeMessageError && <div className="error">{welcomeMessageError}</div>}
                    </StyledFieldInfoText>
                </div>
            </ToggleContents>
        </div>
    );
};

WelcomeMessageToggle.propTypes = {
    isPublished: PropTypes.bool,
    canModifySettings: PropTypes.bool,
    currentBoard: PropTypes.object,
    currentBoardId: PropTypes.string,

    showSharedAncestorBoardLink: PropTypes.bool,
    publishedAncestorBoard: PropTypes.object,

    dispatchSaveWelcomeMessage: PropTypes.func,
    dispatchSetWelcomeMessageEnabled: PropTypes.func,
};

export default connect(null, mapDispatchToProps)(WelcomeMessageToggle);
